import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { BookmarkItem, BookmarkClip, BookmarkDocument } from '@proref/types';
import { useBookmarksQuery, useRemoveBookmark, useRemoveDocumentBookmark } from '../hooks/use-bookmarks';
import { useDownloadDocument } from '../hooks/use-documents';
import { notifyError } from '../lib/toast';
import { PageHeader } from '../components/shared/PageHeader';
import { Skeleton } from '../components/shared/Skeleton';
import { Select } from '../components/shared/Select';
import { Icon } from '../components/shared/Icons';
import { BookmarkNoteEditor } from '../components/bookmarks/BookmarkNoteEditor';

// Bookmark rows narrowed to the target that's actually set (a live clip / document).
type ClipBookmark = BookmarkItem & { clip: BookmarkClip };
type DocumentBookmark = BookmarkItem & { document: BookmarkDocument };

// mm:ss for the thumbnail duration badge.
function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—:—';
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

// Coarse "saved 3d ago" relative label — the exact timestamp doesn't matter on a saved list. A
// plain function, not a component — takes `t` as a parameter, matching every other module-level
// helper in this codebase that needs translated text.
function savedAgo(t: TFunction, iso: string): string {
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  const mins = Math.round(secs / 60);
  const hours = Math.round(mins / 60);
  const days = Math.round(hours / 24);
  if (secs < 60) return t('bookmarks.justNow');
  if (mins < 60) return t('bookmarks.minutesAgo', { count: mins });
  if (hours < 24) return t('bookmarks.hoursAgo', { count: hours });
  if (days < 30) return t('bookmarks.daysAgo', { count: days });
  const months = Math.round(days / 30);
  if (months < 12) return t('bookmarks.monthsAgo', { count: months });
  return t('bookmarks.yearsAgo', { count: Math.round(months / 12) });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type Tab = 'all' | 'clips' | 'documents';
type ClipSort = 'rule' | 'newest';

// The referee's saved clips + documents, grouped into "Saved clips" (each with an editable personal
// note) and "Saved documents" cards, filterable by an All / Clips / Documents tab bar. Saved clips
// can additionally be narrowed to one rule article (RuleFilterSelect) and reordered (By rule /
// Newest, ClipSortControl) — two independent controls: the filter drops non-matching rows, the sort
// only reorders what's left. Clip rows deep-link into the player; document rows download via a
// signed GET. Dead bookmarks (target since deleted) are dropped rather than rendered as a dead row.
export function BookmarkedPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useBookmarksQuery();
  const [tab, setTab] = useState<Tab>('all');
  const [sort, setSort] = useState<ClipSort>('newest');
  // 'all' = no filter. A specific value is a rule_article id — narrows Saved clips to just that
  // rule, distinct from `sort`'s "By rule" which only reorders (kept both: reordering vs.
  // narrowing are different asks, and a referee chasing one weak rule wants the narrowing).
  const [ruleFilter, setRuleFilter] = useState<string>('all');

  const all = useMemo(() => data ?? [], [data]);
  const clips = useMemo(
    () => all.filter((b): b is ClipBookmark => b.clip != null),
    [all],
  );
  const documents = useMemo(
    () => all.filter((b): b is DocumentBookmark => b.document != null),
    [all],
  );

  // Distinct rule articles actually represented among saved clips, numerically sorted — the
  // filter's option list. Untagged clips (rule_article null) don't get an option; there's
  // nothing to narrow to for them.
  const ruleOptions = useMemo(() => {
    const byId = new Map<string, { id: string; article_number: string; title: string }>();
    for (const b of clips) {
      const rule = b.clip.rule_article;
      if (rule && !byId.has(rule.id)) byId.set(rule.id, rule);
    }
    return [...byId.values()].sort((a, b) =>
      a.article_number.localeCompare(b.article_number, undefined, { numeric: true }),
    );
  }, [clips]);

  // Deliberately NOT auto-reset when the selected rule stops being represented (e.g. its last
  // saved clip was unbookmarked): that would silently swap the filter out from under the
  // referee with no explanation. Instead the filtered list just goes to zero and the empty
  // state below offers an explicit "Clear filter" — see filteredClips.

  const filteredClips = useMemo(
    () => (ruleFilter === 'all' ? clips : clips.filter((b) => b.clip.rule_article?.id === ruleFilter)),
    [clips, ruleFilter],
  );

  // Sort the (already rule-filtered) clips list: by rule article number (numeric-aware) or
  // newest-saved first.
  const visibleClips = useMemo(() => {
    const rows = [...filteredClips];
    if (sort === 'newest') {
      rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
    } else {
      rows.sort((a, b) =>
        (a.clip.rule_article?.article_number ?? '').localeCompare(
          b.clip.rule_article?.article_number ?? '',
          undefined,
          { numeric: true },
        ),
      );
    }
    return rows;
  }, [filteredClips, sort]);

  const isEmpty = !isLoading && !isError && all.length === 0;
  const showClips = tab !== 'documents';
  const showDocs = tab !== 'clips';

  return (
    <>
      <PageHeader title={t('bookmarks.title')} sub={t('bookmarks.subtitle')} />

      {!isLoading && !isError && !isEmpty && (
        <TabBar tab={tab} onChange={setTab} counts={{ all: all.length, clips: clips.length, documents: documents.length }} />
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[132px] w-full" />
          ))}
        </div>
      ) : isError && !data ? (
        // Data-fetch failures render inline where the data would be, not as a toast (per conventions).
        <div className="rounded border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('bookmarks.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : isEmpty ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
          <Icon.bookmark className="h-7 w-7 text-muted" />
          <p className="mt-3 text-sm font-medium text-ink">{t('bookmarks.noBookmarksTitle')}</p>
          <p className="mt-1 text-xs text-muted">{t('bookmarks.noBookmarksBody')}</p>
          <Link
            to="/clips"
            className="mt-4 text-xs font-medium text-accent underline underline-offset-2 hover:text-accent-2"
          >
            {t('bookmarks.browseClips')}
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Card stays up whenever there's ANYTHING saved, even if the rule filter currently
              narrows the visible rows to zero — hiding the whole card on a filter miss would
              read as "your bookmarks vanished" rather than "nothing matches this rule". */}
          {showClips && clips.length > 0 && (
            <SectionCard
              title={t('bookmarks.savedClips')}
              sub={t('bookmarks.savedClipsSub')}
              action={
                <div className="flex flex-wrap items-center gap-3">
                  {ruleOptions.length > 1 && (
                    <RuleFilterSelect rules={ruleOptions} value={ruleFilter} onChange={setRuleFilter} />
                  )}
                  <ClipSortControl sort={sort} onChange={setSort} />
                </div>
              }
            >
              {visibleClips.length > 0 ? (
                <div className="space-y-2.5">
                  {visibleClips.map((b) => (
                    <ClipBookmarkRow key={b.id} bookmark={b} />
                  ))}
                </div>
              ) : (
                <p className="rounded border border-dashed border-line-2 bg-bg-2 px-4 py-6 text-center text-xs text-muted">
                  {t('bookmarks.noSavedForRule')}{' '}
                  <button
                    type="button"
                    onClick={() => setRuleFilter('all')}
                    className="font-medium text-accent underline underline-offset-2 hover:text-accent-2"
                  >
                    {t('bookmarks.clearFilter')}
                  </button>
                </p>
              )}
            </SectionCard>
          )}

          {showDocs && documents.length > 0 && (
            <SectionCard title={t('bookmarks.savedDocuments')} sub={t('bookmarks.savedDocumentsSub')}>
              <div className="space-y-2">
                {documents.map((b) => (
                  <DocumentBookmarkRow key={b.id} bookmark={b} />
                ))}
              </div>
            </SectionCard>
          )}

          {/* Tab-specific empty state (e.g. "Documents" selected but none saved). */}
          {showClips && !showDocs && clips.length === 0 && <TabEmpty label={t('bookmarks.noSavedClips')} />}
          {showDocs && !showClips && documents.length === 0 && <TabEmpty label={t('bookmarks.noSavedDocuments')} />}
        </div>
      )}
    </>
  );
}

function TabBar({ tab, onChange, counts }: { tab: Tab; onChange: (t: Tab) => void; counts: Record<Tab, number> }) {
  const { t } = useTranslation();
  const tabs: { key: Tab; label: string }[] = [
    { key: 'all', label: t('bookmarks.tabAll') },
    { key: 'clips', label: t('bookmarks.tabClips') },
    { key: 'documents', label: t('bookmarks.tabDocuments') },
  ];
  return (
    <div className="mb-5 flex flex-wrap gap-2">
      {tabs.map(({ key, label }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={
              'inline-flex items-center gap-2 rounded-btn px-3 py-1.5 text-sm font-medium transition-colors ' +
              (active
                ? 'bg-accent text-accent-ink'
                : 'border border-line-2 bg-surface text-ink hover:border-line-3 hover:bg-surface-2')
            }
          >
            {label}
            <span className={active ? 'text-accent-ink/70' : 'text-muted'}>{counts[key]}</span>
          </button>
        );
      })}
    </div>
  );
}

// Narrows Saved clips to one rule article. Only rendered when 2+ rules are actually represented
// (the caller checks `ruleOptions.length > 1`) — a single-option filter has nothing to narrow.
function RuleFilterSelect({
  rules,
  value,
  onChange,
}: {
  rules: { id: string; article_number: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <Icon.filter className="h-3.5 w-3.5 flex-none text-muted" />
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t('bookmarks.filterByRuleAria')}
        className="h-7 w-auto py-0 text-xs"
      >
        <option value="all">{t('bookmarks.allRules')}</option>
        {rules.map((r) => (
          <option key={r.id} value={r.id}>
            {t('common.ruleLabel', { number: r.article_number })}
          </option>
        ))}
      </Select>
    </div>
  );
}

function SectionCard({
  title,
  sub,
  action,
  children,
}: {
  title: string;
  sub: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-xs text-muted">{sub}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// Sort-only (reorders, never narrows the list) — the actual narrowing lives in RuleFilterSelect
// above. Deliberately text-only, no icon: it used to carry the filter funnel glyph, which now
// sits on the real filter right beside it and would read as two filters.
function ClipSortControl({ sort, onChange }: { sort: ClipSort; onChange: (s: ClipSort) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-none items-center gap-3 text-xs">
      <button
        type="button"
        onClick={() => onChange('rule')}
        className={
          'font-medium transition-colors ' + (sort === 'rule' ? 'text-ink' : 'text-muted hover:text-ink-2')
        }
      >
        {t('bookmarks.byRule')}
      </button>
      <button
        type="button"
        onClick={() => onChange('newest')}
        className={
          'font-medium transition-colors ' + (sort === 'newest' ? 'text-ink' : 'text-muted hover:text-ink-2')
        }
      >
        {t('bookmarks.newest')}
      </button>
    </div>
  );
}

// `label` is already the full translated sentence ("No saved clips yet.") — the two languages
// need it as one string, not "No saved {noun}", since Croatian/Romanian word order or agreement
// between the noun and "no saved" doesn't necessarily follow English's fixed shape.
function TabEmpty({ label }: { label: string }) {
  return (
    <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
      <p className="text-xs text-muted">{label}</p>
    </div>
  );
}

// One saved clip: poster + play/duration on the left, title + rule + saved-time + an editable note in
// the middle, Open / Unsave on the right. The poster and Open both deep-link into the player.
function ClipBookmarkRow({ bookmark }: { bookmark: ClipBookmark }) {
  const { t } = useTranslation();
  const { clip } = bookmark;
  const remove = useRemoveBookmark();
  const href = `/clips?clip=${clip.id}`;

  async function onRemove() {
    try {
      await remove.mutateAsync(clip.id);
    } catch (err) {
      notifyError(err, t('bookmarks.removeErrorToast'));
    }
  }

  return (
    <div className="group flex gap-3 rounded-sm bg-bg-2 p-3">
      <Link
        to={href}
        aria-label={t('home.recommended.openClipAria', { title: clip.title })}
        className="relative aspect-video w-36 flex-none overflow-hidden rounded-sm bg-surface-3 sm:w-40"
      >
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[11px] text-muted">{t('common.noPoster')}</div>
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-ink/50 text-white shadow-1 backdrop-blur transition-colors group-hover:bg-ink/65">
            <Icon.play className="h-3.5 w-3.5" />
          </span>
        </span>
        <span className="absolute bottom-1.5 left-1.5 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[10px] font-medium text-bg">
          {formatDuration(clip.duration_sec)}
        </span>
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div>
          <div className="line-clamp-1 text-[15px] font-medium text-ink">{clip.title}</div>
          <div className="mt-0.5 text-xs text-muted">
            {clip.rule_article ? `${t('common.ruleLabel', { number: clip.rule_article.article_number })} · ` : ''}
            {t('bookmarks.savedPrefix', { time: savedAgo(t, bookmark.created_at) })}
          </div>
        </div>
        <BookmarkNoteEditor bookmarkId={bookmark.id} note={bookmark.note} />
      </div>

      <div className="flex flex-none flex-col items-end gap-2">
        <Link
          to={href}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-btn border-[1.5px] border-line-2 bg-surface px-3 text-xs font-medium text-ink transition-colors hover:border-line-3 hover:bg-surface-2"
        >
          {t('bookmarks.open')}
        </Link>
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-btn px-2.5 text-xs font-medium text-muted transition-colors hover:text-danger"
        >
          <Icon.bookmark className="h-3.5 w-3.5 fill-current" />
          {t('bookmarks.unsave')}
        </button>
      </div>
    </div>
  );
}

// One saved document: PDF icon, title, "Updated <date> · <size>", Unsave + Open (download).
function DocumentBookmarkRow({ bookmark }: { bookmark: DocumentBookmark }) {
  const { t } = useTranslation();
  const { document: doc } = bookmark;
  const remove = useRemoveDocumentBookmark();
  const download = useDownloadDocument();

  async function onRemove() {
    try {
      await remove.mutateAsync(doc.id);
    } catch (err) {
      notifyError(err, t('bookmarks.removeErrorToast'));
    }
  }

  async function onOpen() {
    try {
      const url = await download.mutateAsync(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      notifyError(err, t('bookmarks.openErrorToast'));
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-sm bg-bg-2 p-3">
      <span className="grid h-10 w-10 flex-none place-items-center rounded-sm bg-surface text-ink-3 shadow-1">
        <Icon.doc className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="line-clamp-1 text-sm font-medium text-ink">{doc.title}</div>
        <div className="mt-0.5 text-[11.5px] text-muted">
          {t('bookmarks.updatedLabel', { date: formatDate(doc.created_at) })}
          {doc.file_size_bytes != null ? ` · ${formatSize(doc.file_size_bytes)}` : ''}
        </div>
      </div>

      <div className="flex flex-none items-center gap-1.5">
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('bookmarks.unsaveAria', { title: doc.title })}
          className="grid h-8 w-8 place-items-center rounded-btn text-muted transition-colors hover:text-danger"
        >
          <Icon.bookmark className="h-4 w-4 fill-current" />
        </button>
        <button
          type="button"
          onClick={onOpen}
          disabled={download.isPending}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-btn border-[1.5px] border-line-2 bg-surface px-3 text-xs font-medium text-ink transition-colors hover:border-line-3 hover:bg-surface-2 disabled:opacity-50"
        >
          {t('bookmarks.open')}
        </button>
      </div>
    </div>
  );
}
